---
title: 'RPC endpoint hosting cost: AWS vs dedicated infrastructure in 2026'
slug: 'rpc-endpoint-hosting-cost-aws-vs-dedicated'
date: '2026-05-13'
description: 'A single RPC endpoint workload costs $1,112 per month on AWS and $194 on dedicated component-priced infrastructure. Side-by-side pricing across AWS, Hetzner, Latitude, Servers.com, and LinkPool with the egress and state-bloat context behind the gap.'
author: 'LinkPool Team'
---

A single RPC endpoint workload (16 vCPU, 64 GB RAM, 4 TB NVMe net of replication) costs $1,112 per month on AWS. The same workload runs at $194 per month on dedicated component-priced infrastructure. That gap is wider than the equivalent gap for an Ethereum validator or a Chainlink node, and the reason is network egress. RPC endpoints are the only crypto infrastructure workload where bandwidth is a meaningful monthly cost driver, and bandwidth is the line hyperscalers price most aggressively.

This page runs the actual numbers for one RPC endpoint workload across the providers an operator actually compares, then explains why the gap looks the way it does.

## The workload

A production RPC endpoint at modest institutional scale needs:

- 16 vCPU steady-state under JSON-RPC load
- 64 GB RAM at the archive-feature ceiling for Ethereum execution plus consensus client memory plus JSON-RPC connection pool overhead
- 4 TB NVMe net of replication for state, recent trie depth, recent block storage
- continuous high-bandwidth ingress and egress for JSON-RPC traffic
- 99.99% uptime target

Most RPC cost comparisons treat the workload as "big servers." The cost shape is actually unusual. CPU sits high under sustained load instead of spiking. Memory hits a ceiling that does not move. Storage grows aggressively with archive features and recent trie depth. Egress dominates the bill on public RPC endpoints in a way it does not for any other crypto workload.

## Side-by-side pricing

All monthly figures. Source: linkpool.io/pricing canonical workload table.

| Provider | Monthly cost | Pricing model |
|---|---|---|
| AWS EC2 | $1,112 | Per-resource, egress on top |
| Servers.com SBM | $1,219 | Whole server, smallest SBM that fits |
| Latitude.sh | $720 | Whole server |
| Hetzner AX162 | $219 | Whole server, fits one workload |
| LinkPool Standard 12-month | $194 | Component, bundled egress |
| LinkPool Performance 12-month | $335 | Component, guaranteed QoS |

Two numbers stand out. AWS is more than 5x LinkPool Standard at this workload size. Hetzner is the only other low-cost option and it sells one server, not one workload. The economics under each model are not the same conversation.

## Why the gap is wider for RPC than for validators

Egress is the single biggest difference. AWS data transfer out runs $0.09 per GB after the first GB. A moderately-loaded public RPC endpoint serves multiple terabytes of egress per month under sustained JSON-RPC traffic. That alone is several hundred dollars per month before any compute, memory, or storage cost.

State bloat is the second. The 4 TB net figure today is projected to climb to 5-6 TB by the end of 2026 for archive-feature endpoints. NVMe at scale is priced differently on a hyperscaler than on a colocation host.

Read-replica scaling is the third. Hyperscalers price read replicas aggressively. Dedicated infrastructure runs replicas on the same fabric without per-replica licensing or egress between zones.

## When each option is the right call

AWS suits short-lived test and development RPC setups, integration environments, and any case where the workload runs less than full-time. The hourly billing model and the surrounding service catalogue carry real value when usage is bursty.

Hetzner is the right call for operators running one or two RPC endpoints per server, accepting the platform risk that comes with consumer-grade infrastructure for production crypto work. In November 2022 Hetzner enforced its terms-of-service against 1,000+ Solana validators in a single policy change. That risk has not gone away.

LinkPool Standard fits production RPC work where steady-state cost matters and traffic is predictable. Standard is burstable QoS, which RPC workloads handle well because their CPU sits high under load rather than spiking.

LinkPool Performance fits RPC work where any throttling under contention or any read-replica scaling pressure is unacceptable. Institutional ETH endpoint operators, large dApp backends, and any workload sensitive to JSON-RPC tail latency belong on Performance.

## What else changes the bill

Three line items that do not appear in a sticker-price comparison but matter at the bill level.

Hypervisor overhead adds 0.3-1.2ms per network hop on shared-tenant hosts. For JSON-RPC traffic that is a measurable tail-latency tax.

DIY dedicated-infrastructure operations overhead runs £4,000-£13,000 per month in engineering time on a self-managed build. That figure does not appear in a per-server quote. It does appear in the headcount budget.

Manchester. Our infrastructure runs across three availability zones in Manchester. That is the location reference operators audit when an institutional buyer asks where the workload runs.

## How to compare against your current footprint

The useful comparison is not provider-vs-provider, it is current-footprint-vs-component-pricing. Pull your current public-RPC monthly cost including egress. Map the workload shape to one of the rows above. The right next question is which pricing model fits your traffic curve, not which provider has the lowest sticker price.

## Related reading

- [ETH validator hosting cost: AWS vs dedicated infrastructure in 2026](/blog/eth-validator-hosting-cost-aws-vs-dedicated), page 1 of this comparison series
- [Chainlink node hosting cost: AWS vs dedicated infrastructure in 2026](/blog/chainlink-node-hosting-cost-aws-vs-dedicated), page 2 of this comparison series
- [Bare-metal Kubernetes hosting: cloud vs dedicated infrastructure](/blog/bare-metal-kubernetes-hosting), the architecture context
- [What goes into running a Chainlink Data Streams node](/blog/chainlink-data-streams-node-operations), shared workload-shape framing
